import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Building2, Shield, FileCheck, DollarSign, 
  ArrowRight, ArrowLeft, Check, AlertTriangle,
  Briefcase, Phone, Globe, Award, Users, Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import Navbar from "../components/Navbar";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const categories = [
  { id: "dj", name: "DJ" },
  { id: "musician", name: "Musician" },
  { id: "singer", name: "Singer" },
  { id: "mc", name: "MC / Host" },
  { id: "comedian", name: "Comedian" },
  { id: "photographer", name: "Photographer" },
  { id: "caterer", name: "Caterer" },
  { id: "baker", name: "Baker" },
  { id: "decorator", name: "Decorator" },
  { id: "balloon_artist", name: "Balloon Artist" },
  { id: "security", name: "Event Security" },
];

const businessTypes = [
  { id: "sole_proprietor", name: "Sole Proprietor" },
  { id: "llc", name: "LLC" },
  { id: "corporation", name: "Corporation" },
  { id: "partnership", name: "Partnership" },
];

const usStates = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", 
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const VendorApplication = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [agreementTerms, setAgreementTerms] = useState("");
  const [existingApplication, setExistingApplication] = useState(null);
  
  const [formData, setFormData] = useState({
    // Business Info
    business_name: "",
    category: "",
    description: "",
    location: "",
    city: "",
    state: "",
    zip_code: "",
    hourly_rate: "",
    services: "",
    years_in_business: "",
    business_type: "",
    ein_number: "",
    website: "",
    social_media: { instagram: "", facebook: "", twitter: "" },
    // License Info
    has_business_license: false,
    business_license_number: "",
    business_license_state: "",
    business_license_expiry: "",
    professional_certifications: "",
    // Insurance Info
    has_liability_insurance: false,
    insurance_provider: "",
    insurance_policy_number: "",
    insurance_coverage_amount: "",
    insurance_expiry_date: "",
    // Contact & References
    portfolio_urls: "",
    references: [
      { name: "", phone: "", relationship: "" },
      { name: "", phone: "", relationship: "" }
    ],
    emergency_contact_name: "",
    emergency_contact_phone: "",
    // Agreements
    agreed_to_terms: false,
    agreed_to_noncompete: false,
    agreed_to_service_fee: false
  });

  useEffect(() => {
    if (!user) {
      navigate("/auth", { state: { from: "/vendor-application" } });
      return;
    }
    fetchAgreementTerms();
    checkExistingApplication();
  }, [user]);

  const fetchAgreementTerms = async () => {
    try {
      const response = await axios.get(`${API}/vendors/agreement-terms`);
      setAgreementTerms(response.data.terms);
    } catch (error) {
      console.error("Error fetching terms:", error);
    }
  };

  const checkExistingApplication = async () => {
    try {
      const response = await axios.get(`${API}/vendors/my/application`, { withCredentials: true });
      setExistingApplication(response.data);
    } catch (error) {
      // No existing application
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleReferenceChange = (index, field, value) => {
    const newRefs = [...formData.references];
    newRefs[index] = { ...newRefs[index], [field]: value };
    setFormData(prev => ({ ...prev, references: newRefs }));
  };

  const handleSocialChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      social_media: { ...prev.social_media, [platform]: value }
    }));
  };

  const handleSubmit = async () => {
    if (!formData.agreed_to_terms || !formData.agreed_to_noncompete || !formData.agreed_to_service_fee) {
      toast.error("Please agree to all terms and conditions");
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        location: `${formData.city}, ${formData.state}`,
        hourly_rate: parseFloat(formData.hourly_rate),
        years_in_business: parseInt(formData.years_in_business),
        services: formData.services.split(",").map(s => s.trim()).filter(Boolean),
        professional_certifications: formData.professional_certifications.split(",").map(s => s.trim()).filter(Boolean),
        portfolio_urls: formData.portfolio_urls.split(",").map(s => s.trim()).filter(Boolean),
        insurance_coverage_amount: formData.insurance_coverage_amount ? parseFloat(formData.insurance_coverage_amount) : null,
        references: formData.references.filter(r => r.name && r.phone),
      };

      await axios.post(`${API}/vendors/apply`, submitData, { withCredentials: true });
      toast.success("Application submitted! We'll review it within 2-3 business days and notify you by email.");
      navigate("/vendor-application"); // Redirect to see application status
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  // Show existing application status
  if (existingApplication) {
    return (
      <div className="min-h-screen bg-obsidian">
        <Navbar />
        <div className="pt-28 pb-12 px-6">
          <div className="container mx-auto max-w-2xl">
            <div className="bg-charcoal rounded-2xl border border-white/5 p-8 text-center">
              <div className={`w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center ${
                existingApplication.status === "approved" ? "bg-green-500/10" :
                existingApplication.status === "rejected" ? "bg-red-500/10" :
                "bg-yellow-500/10"
              }`}>
                {existingApplication.status === "approved" ? (
                  <Check className="w-8 h-8 text-green-500" />
                ) : existingApplication.status === "rejected" ? (
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                ) : (
                  <FileCheck className="w-8 h-8 text-yellow-500" />
                )}
              </div>
              
              <h1 className="font-heading text-2xl font-bold text-white mb-2">
                Application {existingApplication.status === "pending" ? "Under Review" : 
                            existingApplication.status === "approved" ? "Approved!" : "Status"}
              </h1>
              
              <p className="text-white/60 mb-6">
                {existingApplication.status === "pending" && "Your vendor application is being reviewed. We'll notify you within 2-3 business days."}
                {existingApplication.status === "under_review" && "Your application is currently under review by our team."}
                {existingApplication.status === "approved" && "Congratulations! Your application has been approved. You can now access your vendor dashboard."}
                {existingApplication.status === "rejected" && "Unfortunately, your application was not approved at this time."}
              </p>

              {/* What's Next Info Box - shown for pending/under_review */}
              {(existingApplication.status === "pending" || existingApplication.status === "under_review") && (
                <div className="bg-gold/10 border border-gold/30 rounded-xl p-5 mb-6 text-left">
                  <h3 className="font-heading font-bold text-gold mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    What Happens Next?
                  </h3>
                  <ul className="text-white/70 text-sm space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">1.</span>
                      <span>Our team reviews your business information, licenses, and insurance details</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">2.</span>
                      <span>You'll receive an email notification once your application is reviewed</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">3.</span>
                      <span>If approved, you can immediately start receiving bookings through your vendor dashboard</span>
                    </li>
                  </ul>
                  <p className="text-white/50 text-xs mt-4 pt-3 border-t border-white/10">
                    Questions? Contact us at support@rafhaentertainment.com
                  </p>
                </div>
              )}
              
              {existingApplication.admin_notes && (
                <div className="bg-obsidian rounded-xl p-4 mb-6 text-left">
                  <p className="text-white/40 text-sm mb-1">Admin Notes:</p>
                  <p className="text-white/80">{existingApplication.admin_notes}</p>
                </div>
              )}
              
              <Button
                onClick={() => navigate("/dashboard")}
                className="bg-gold text-black hover:bg-gold-light rounded-full font-bold"
              >
                Go to Dashboard
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalSteps = 5;

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      <div className="pt-28 pb-12 px-6">
        <div className="container mx-auto max-w-3xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="font-heading text-3xl font-bold text-white mb-2">
              Become a <span className="text-gold">Rafha Vendor</span>
            </h1>
            <p className="text-white/60">
              Join our network of trusted entertainment professionals
            </p>
          </motion.div>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className="flex items-center">
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                    step >= s ? "bg-gold text-black" : "bg-white/10 text-white/40"
                  }`}
                >
                  {step > s ? <Check className="w-5 h-5" /> : s}
                </div>
                {s < totalSteps && (
                  <div className={`w-8 h-0.5 ${step > s ? "bg-gold" : "bg-white/10"}`}></div>
                )}
              </div>
            ))}
          </div>
          
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-charcoal rounded-2xl border border-white/5 p-8"
          >
            {/* Step 1: Business Information */}
            {step === 1 && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-white">Business Information</h2>
                    <p className="text-white/60 text-sm">Tell us about your business</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/60">Business Name *</Label>
                      <Input
                        value={formData.business_name}
                        onChange={(e) => handleInputChange("business_name", e.target.value)}
                        className="mt-1 bg-obsidian border-white/10 text-white"
                        data-testid="app-business-name"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60">Category *</Label>
                      <Select value={formData.category} onValueChange={(v) => handleInputChange("category", v)}>
                        <SelectTrigger className="mt-1 bg-obsidian border-white/10 text-white" data-testid="app-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent className="bg-charcoal border-white/10">
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id} className="text-white">{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-white/60">Business Description *</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange("description", e.target.value)}
                      rows={3}
                      className="mt-1 bg-obsidian border-white/10 text-white"
                      placeholder="Describe your services, experience, and what makes you unique..."
                      data-testid="app-description"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-white/60">City *</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => handleInputChange("city", e.target.value)}
                        className="mt-1 bg-obsidian border-white/10 text-white"
                        data-testid="app-city"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60">State *</Label>
                      <Select value={formData.state} onValueChange={(v) => handleInputChange("state", v)}>
                        <SelectTrigger className="mt-1 bg-obsidian border-white/10 text-white" data-testid="app-state">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="bg-charcoal border-white/10 max-h-60">
                          {usStates.map((state) => (
                            <SelectItem key={state} value={state} className="text-white">{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white/60">ZIP Code *</Label>
                      <Input
                        value={formData.zip_code}
                        onChange={(e) => handleInputChange("zip_code", e.target.value)}
                        className="mt-1 bg-obsidian border-white/10 text-white"
                        data-testid="app-zip"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/60">Hourly Rate ($) *</Label>
                      <Input
                        type="number"
                        value={formData.hourly_rate}
                        onChange={(e) => handleInputChange("hourly_rate", e.target.value)}
                        className="mt-1 bg-obsidian border-white/10 text-white"
                        data-testid="app-hourly-rate"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60">Years in Business *</Label>
                      <Input
                        type="number"
                        value={formData.years_in_business}
                        onChange={(e) => handleInputChange("years_in_business", e.target.value)}
                        className="mt-1 bg-obsidian border-white/10 text-white"
                        data-testid="app-years"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/60">Business Type *</Label>
                      <Select value={formData.business_type} onValueChange={(v) => handleInputChange("business_type", v)}>
                        <SelectTrigger className="mt-1 bg-obsidian border-white/10 text-white" data-testid="app-business-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="bg-charcoal border-white/10">
                          {businessTypes.map((type) => (
                            <SelectItem key={type.id} value={type.id} className="text-white">{type.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white/60">EIN Number (Optional)</Label>
                      <Input
                        value={formData.ein_number}
                        onChange={(e) => handleInputChange("ein_number", e.target.value)}
                        className="mt-1 bg-obsidian border-white/10 text-white"
                        placeholder="XX-XXXXXXX"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-white/60">Services Offered (comma-separated) *</Label>
                    <Input
                      value={formData.services}
                      onChange={(e) => handleInputChange("services", e.target.value)}
                      className="mt-1 bg-obsidian border-white/10 text-white"
                      placeholder="Wedding DJ, Corporate Events, Birthday Parties"
                      data-testid="app-services"
                    />
                  </div>
                </div>
              </>
            )}
            
            {/* Step 2: License Information */}
            {step === 2 && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                    <Award className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-white">License & Certifications</h2>
                    <p className="text-white/60 text-sm">Professional credentials</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 p-4 bg-obsidian rounded-xl">
                    <Checkbox
                      id="has_license"
                      checked={formData.has_business_license}
                      onCheckedChange={(checked) => handleInputChange("has_business_license", checked)}
                      className="border-gold data-[state=checked]:bg-gold"
                      data-testid="app-has-license"
                    />
                    <Label htmlFor="has_license" className="text-white cursor-pointer">
                      I have a valid business license
                    </Label>
                  </div>
                  
                  {formData.has_business_license && (
                    <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-gold/30">
                      <div>
                        <Label className="text-white/60">License Number</Label>
                        <Input
                          value={formData.business_license_number}
                          onChange={(e) => handleInputChange("business_license_number", e.target.value)}
                          className="mt-1 bg-obsidian border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white/60">Issuing State</Label>
                        <Select value={formData.business_license_state} onValueChange={(v) => handleInputChange("business_license_state", v)}>
                          <SelectTrigger className="mt-1 bg-obsidian border-white/10 text-white">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent className="bg-charcoal border-white/10 max-h-60">
                            {usStates.map((state) => (
                              <SelectItem key={state} value={state} className="text-white">{state}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-white/60">Expiration Date</Label>
                        <Input
                          type="date"
                          value={formData.business_license_expiry}
                          onChange={(e) => handleInputChange("business_license_expiry", e.target.value)}
                          className="mt-1 bg-obsidian border-white/10 text-white"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <Label className="text-white/60">Professional Certifications (comma-separated)</Label>
                    <Input
                      value={formData.professional_certifications}
                      onChange={(e) => handleInputChange("professional_certifications", e.target.value)}
                      className="mt-1 bg-obsidian border-white/10 text-white"
                      placeholder="e.g., ServSafe, CPR Certified, DJ Certification"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white/60">Website (Optional)</Label>
                    <Input
                      value={formData.website}
                      onChange={(e) => handleInputChange("website", e.target.value)}
                      className="mt-1 bg-obsidian border-white/10 text-white"
                      placeholder="https://yourbusiness.com"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-white/60">Instagram</Label>
                      <Input
                        value={formData.social_media.instagram}
                        onChange={(e) => handleSocialChange("instagram", e.target.value)}
                        className="mt-1 bg-obsidian border-white/10 text-white"
                        placeholder="@username"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60">Facebook</Label>
                      <Input
                        value={formData.social_media.facebook}
                        onChange={(e) => handleSocialChange("facebook", e.target.value)}
                        className="mt-1 bg-obsidian border-white/10 text-white"
                        placeholder="Page URL"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60">Twitter/X</Label>
                      <Input
                        value={formData.social_media.twitter}
                        onChange={(e) => handleSocialChange("twitter", e.target.value)}
                        className="mt-1 bg-obsidian border-white/10 text-white"
                        placeholder="@username"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* Step 3: Insurance Information */}
            {step === 3 && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-white">Insurance Information</h2>
                    <p className="text-white/60 text-sm">Liability coverage details</p>
                  </div>
                </div>
                
                <div className="bg-fire-orange/10 border border-fire-orange/30 rounded-xl p-4 mb-6">
                  <p className="text-fire-orange text-sm">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    <strong>Important:</strong> We strongly recommend maintaining liability insurance coverage of at least $1,000,000. This protects both you and your clients.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 p-4 bg-obsidian rounded-xl">
                    <Checkbox
                      id="has_insurance"
                      checked={formData.has_liability_insurance}
                      onCheckedChange={(checked) => handleInputChange("has_liability_insurance", checked)}
                      className="border-gold data-[state=checked]:bg-gold"
                      data-testid="app-has-insurance"
                    />
                    <Label htmlFor="has_insurance" className="text-white cursor-pointer">
                      I have liability insurance coverage
                    </Label>
                  </div>
                  
                  {formData.has_liability_insurance && (
                    <div className="space-y-4 pl-4 border-l-2 border-gold/30">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-white/60">Insurance Provider</Label>
                          <Input
                            value={formData.insurance_provider}
                            onChange={(e) => handleInputChange("insurance_provider", e.target.value)}
                            className="mt-1 bg-obsidian border-white/10 text-white"
                            placeholder="e.g., State Farm, Allstate"
                          />
                        </div>
                        <div>
                          <Label className="text-white/60">Policy Number</Label>
                          <Input
                            value={formData.insurance_policy_number}
                            onChange={(e) => handleInputChange("insurance_policy_number", e.target.value)}
                            className="mt-1 bg-obsidian border-white/10 text-white"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-white/60">Coverage Amount ($)</Label>
                          <Input
                            type="number"
                            value={formData.insurance_coverage_amount}
                            onChange={(e) => handleInputChange("insurance_coverage_amount", e.target.value)}
                            className="mt-1 bg-obsidian border-white/10 text-white"
                            placeholder="1000000"
                          />
                        </div>
                        <div>
                          <Label className="text-white/60">Expiration Date</Label>
                          <Input
                            type="date"
                            value={formData.insurance_expiry_date}
                            onChange={(e) => handleInputChange("insurance_expiry_date", e.target.value)}
                            className="mt-1 bg-obsidian border-white/10 text-white"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
            
            {/* Step 4: References & Portfolio */}
            {step === 4 && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-white">References & Portfolio</h2>
                    <p className="text-white/60 text-sm">Help us verify your work history</p>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <Label className="text-white/60">Portfolio Links (comma-separated)</Label>
                    <Input
                      value={formData.portfolio_urls}
                      onChange={(e) => handleInputChange("portfolio_urls", e.target.value)}
                      className="mt-1 bg-obsidian border-white/10 text-white"
                      placeholder="https://yourportfolio.com, https://instagram.com/yourwork"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white mb-3 block">Professional References (at least 2 recommended)</Label>
                    {formData.references.map((ref, i) => (
                      <div key={i} className="grid grid-cols-3 gap-3 mb-3 p-4 bg-obsidian rounded-xl">
                        <div>
                          <Label className="text-white/60 text-sm">Name</Label>
                          <Input
                            value={ref.name}
                            onChange={(e) => handleReferenceChange(i, "name", e.target.value)}
                            className="mt-1 bg-charcoal border-white/10 text-white"
                            placeholder="Full name"
                          />
                        </div>
                        <div>
                          <Label className="text-white/60 text-sm">Phone</Label>
                          <Input
                            value={ref.phone}
                            onChange={(e) => handleReferenceChange(i, "phone", e.target.value)}
                            className="mt-1 bg-charcoal border-white/10 text-white"
                            placeholder="(555) 555-5555"
                          />
                        </div>
                        <div>
                          <Label className="text-white/60 text-sm">Relationship</Label>
                          <Input
                            value={ref.relationship}
                            onChange={(e) => handleReferenceChange(i, "relationship", e.target.value)}
                            className="mt-1 bg-charcoal border-white/10 text-white"
                            placeholder="e.g., Former Client"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="border-t border-white/5 pt-6">
                    <Label className="text-white mb-3 block">Emergency Contact</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white/60 text-sm">Contact Name</Label>
                        <Input
                          value={formData.emergency_contact_name}
                          onChange={(e) => handleInputChange("emergency_contact_name", e.target.value)}
                          className="mt-1 bg-obsidian border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white/60 text-sm">Contact Phone</Label>
                        <Input
                          value={formData.emergency_contact_phone}
                          onChange={(e) => handleInputChange("emergency_contact_phone", e.target.value)}
                          className="mt-1 bg-obsidian border-white/10 text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* Step 5: Legal Agreement */}
            {step === 5 && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                    <FileCheck className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-white">Terms & Agreement</h2>
                    <p className="text-white/60 text-sm">Review and accept our vendor agreement</p>
                  </div>
                </div>
                
                <div className="bg-obsidian rounded-xl border border-white/10 mb-6">
                  <ScrollArea className="h-64 p-4">
                    <pre className="text-white/70 whitespace-pre-wrap font-sans text-sm leading-relaxed">
                      {agreementTerms}
                    </pre>
                  </ScrollArea>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-gold/10 border border-gold/30 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-5 h-5 text-gold" />
                      <span className="font-bold text-gold">10% Service Fee</span>
                    </div>
                    <p className="text-white/60 text-sm">
                      A 10% service fee applies to all bookings completed through the Rafha Entertainment platform. This fee is automatically deducted from your payments.
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3 p-4 bg-obsidian rounded-xl">
                      <Checkbox
                        id="agree_terms"
                        checked={formData.agreed_to_terms}
                        onCheckedChange={(checked) => handleInputChange("agreed_to_terms", checked)}
                        className="border-gold data-[state=checked]:bg-gold mt-0.5"
                        data-testid="agree-terms"
                      />
                      <Label htmlFor="agree_terms" className="text-white/80 cursor-pointer text-sm">
                        I have read and agree to the <span className="text-gold">Vendor Agreement Terms</span>, including the indemnification, limitation of liability, and dispute resolution clauses.
                      </Label>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-4 bg-obsidian rounded-xl">
                      <Checkbox
                        id="agree_noncompete"
                        checked={formData.agreed_to_noncompete}
                        onCheckedChange={(checked) => handleInputChange("agreed_to_noncompete", checked)}
                        className="border-gold data-[state=checked]:bg-gold mt-0.5"
                        data-testid="agree-noncompete"
                      />
                      <Label htmlFor="agree_noncompete" className="text-white/80 cursor-pointer text-sm">
                        I agree to the <span className="text-gold">Non-Compete Clause</span> and will not solicit clients introduced through Rafha Entertainment outside of the platform.
                      </Label>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-4 bg-obsidian rounded-xl">
                      <Checkbox
                        id="agree_fee"
                        checked={formData.agreed_to_service_fee}
                        onCheckedChange={(checked) => handleInputChange("agreed_to_service_fee", checked)}
                        className="border-gold data-[state=checked]:bg-gold mt-0.5"
                        data-testid="agree-fee"
                      />
                      <Label htmlFor="agree_fee" className="text-white/80 cursor-pointer text-sm">
                        I understand and agree to the <span className="text-gold">10% Service Fee</span> on all bookings completed through the platform.
                      </Label>
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-white/5">
              {step > 1 ? (
                <Button
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  className="border-white/10 text-white hover:bg-white/5 rounded-full"
                >
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  Back
                </Button>
              ) : (
                <div></div>
              )}
              
              {step < totalSteps ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  className="bg-gold text-black hover:bg-gold-light rounded-full font-bold"
                  data-testid="next-step-btn"
                >
                  Continue
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !formData.agreed_to_terms || !formData.agreed_to_noncompete || !formData.agreed_to_service_fee}
                  className="bg-gold text-black hover:bg-gold-light rounded-full font-bold gold-glow"
                  data-testid="submit-application-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      Submit Application
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default VendorApplication;
